// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsSims from '../../../api/actionsSims';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import FormDetails from './components/FormDetails';
import FooterClose from '@/common-utils/components/renders/modal/components/footers/FooterClose/FooterClose';
import FormAddress from './components/FormAddress';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Style
import './ModalOrderSimCard.scss';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @returns {JSX} ModalOrderSimCard
 */
function ModalOrderSimCard({ isOpen, setIsOpen }) {

    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.order_sim.steps';

    // Store
    const { provider, uuid, legalName } = useRootOrganizationStore(s => s.rootOrganization);

    // State
    const [step, setStep] = useState(0);
    const [formDetailsData, setFormDetailsData] = useState({ isValid: false });
    const [formAddressData, setFormAddressData] = useState({ isValid: false });
    const [requestSent, setRequestSent] = useState(false);
    const [errorOrder, setErrorOrder] = useState(false);

    // Api calls
    const { data, isFetching: isFetchingProviders } = actionsSims.getSimProviders(true);

    const onSuccess = () => {
    };

    const onError = () => {
        setErrorOrder(true);
    };
    const submitOrderSimCard = actionsSims.orderSimCard(onSuccess, onError);

    // Forms data
    const { civility, contactName, contactSurname, companyName, phoneNumber, address, streetNumber, postCode, city, additionalAddress } = formAddressData;
    const { quantity, simProvider } = formDetailsData;

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.details`, disabledNext: !formDetailsData.isValid || !formAddressData.isValid },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.confirmation`, disabledNext: false }
        }),
        [formAddressData, formDetailsData]
    );

    // Render
    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-3xl'>
                <HeaderTitle title='modal.order_sim.title' namespace={nsSimCard} />
                <ModalBody className='modal-max-height overflow-auto p-4'>
                    {!requestSent &&
                        <Entity
                            entity={stepTitle}
                            data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                            namespace={nsSimCard}
                            className='mb-2 flex'
                        />}
                    {step === 0 &&
                        <>
                            <div className='form-card-wrapper p-4 space-y-3 my-4'>
                                <div className='text-center'>
                                    <Entity entity='modal.order_sim.details' namespace={nsSimCard} />
                                </div>
                                {isFetchingProviders && <Spinner className='panel-spinner' />}
                                {!isFetchingProviders && data &&
                                    <FormDetails
                                        simProviders={data?.simProviders}
                                        setFormData={setFormDetailsData}
                                        defaultValues={formDetailsData}
                                    />
                                }
                            </div>
                            <div className='form-card-wrapper p-4 space-y-3'>
                                <div className='text-center'>
                                    <Entity entity='modal.order_sim.address' namespace={nsSimCard} />
                                </div>
                                <FormAddress
                                    organizationName={legalName}
                                    setFormData={setFormAddressData}
                                    defaultValues={formAddressData}
                                />
                            </div>
                        </>
                    }
                    {step === 1 &&
                        <>
                            {submitOrderSimCard.isLoading &&
                                (
                                    <Spinner className='panel-spinner' />
                                )
                            }
                            {!submitOrderSimCard.isLoading && requestSent &&
                                (
                                    <div className='text-center'>
                                        {
                                            errorOrder ?
                                                <Entity entity='modal.order_sim.error' namespace={nsSimCard} />
                                                :
                                                <div>
                                                    <div style={{ marginBottom: '10px' }}>
                                                        <Entity entity='modal.order_sim.validation.text1' namespace={nsSimCard} />
                                                    </div>
                                                    <div style={{ marginBottom: '10px' }}>
                                                        <Entity entity='modal.order_sim.validation.text2' namespace={nsSimCard} />
                                                    </div>
                                                    <div>
                                                        <Entity entity='modal.order_sim.validation.text3' namespace={nsSimCard} />
                                                    </div>
                                                </div>
                                        }
                                    </div>
                                )
                            }
                            {!submitOrderSimCard.isLoading && !requestSent &&
                                (
                                    <div className='form-card-wrapper p-4 mt-4 space-y-3'>
                                        <div className='text-center'>
                                            <Entity entity='modal.order_sim.summary.title' namespace={nsSimCard} />
                                        </div>
                                        <div className='form-card-wrapper p-4 grid grid-cols-[40%_60%] items-center'>
                                            <Entity entity='modal.order_sim.summary.keys.quantity' namespace={nsSimCard} />
                                            <span>{quantity}</span>
                                        </div>
                                        <div className='form-card-wrapper p-4 grid grid-cols-[40%_60%] gap-1 items-center'>
                                            <Entity entity='modal.order_sim.summary.keys.civility' namespace={nsSimCard} />
                                            <span>{civility}</span>
                                            <Entity entity='modal.order_sim.summary.keys.contact_surname' namespace={nsSimCard} />
                                            <span>{contactSurname}</span>
                                            <Entity entity='modal.order_sim.summary.keys.contact_name' namespace={nsSimCard} />
                                            <span>{contactName}</span>
                                            <Entity entity='modal.order_sim.summary.keys.phone_number' namespace={nsSimCard} />
                                            <span>{phoneNumber}</span>
                                        </div>
                                        <div className='form-card-wrapper p-4 grid grid-cols-[40%_60%] items-center'>
                                            <Entity entity='modal.order_sim.summary.keys.company' namespace={nsSimCard} />
                                            <span>{companyName}</span>
                                            <Entity entity='modal.order_sim.summary.keys.street' namespace={nsSimCard} />
                                            <span>{address}</span>
                                            <Entity entity='modal.order_sim.summary.keys.post_code' namespace={nsSimCard} />
                                            <span>{postCode}</span>
                                            <Entity entity='modal.order_sim.summary.keys.city' namespace={nsSimCard} />
                                            <span>{city}</span>
                                        </div>
                                    </div>
                                )
                            }
                        </>
                    }
                </ModalBody>
                {requestSent && <FooterClose />}
                {!requestSent &&
                    <FooterStep
                        step={step}
                        setStep={setStep}
                        stepsNumber={STEPS_NUMBER}
                        onValidate={() => {
                            const dataToSend = {
                                provider: provider,
                                client: uuid,
                                legalName: companyName.toUpperCase(),
                                phoneNumber: phoneNumber,
                                address: `${streetNumber}${address}`.toUpperCase(),
                                additionalAddress: additionalAddress ? additionalAddress.toUpperCase() : '',
                                zipCode: postCode,
                                city: city.toUpperCase(),
                                country: 'France',
                                quantity: parseInt(quantity),
                                simProvider: simProvider,
                                title: civility,
                                firstName: contactSurname,
                                lastName: contactName
                            };
                            submitOrderSimCard.mutate(dataToSend);
                            setRequestSent(true);
                        }}
                        isLoading={submitOrderSimCard.isLoading}
                        disableValidation={disabledNext}
                    />
                }
            </ModalContent>
        </Modal>
    );
}

ModalOrderSimCard.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired
};
export default ModalOrderSimCard;